// ─────────────────────────────────────────────────────────────────────────────
// AOTD gallery images: thumbnail URLs and the per-year sprite.
//
// The grid used to pull every card as a 1000px JPEG through axios (a token
// check per image, a blob URL that was never released, no browser cache).
// Cards now load `/thumb/:filename` (512px WebP, cacheable) as a plain <img>.
//
// The backend also packs every day of a year into one sprite: 40px cells,
// 32 per row, cell `i` = day of the year (Jan 1 = 0, UTC, from the filename).
// The calendar view draws its tiles from it, and the grid uses the same cell,
// blurred, as the instant placeholder while a thumbnail loads.
// The backend computes the cell with the same rule
// (modules/aotd/services/gallerySprite/cellIndex.ts); keep the two in step.
// ─────────────────────────────────────────────────────────────────────────────

import { SERVER_URL } from "../../config/env";

const AOTD_API = `${SERVER_URL}/api/art-of-the-day`;

export const thumbUrl = (filename: string) =>
  `${AOTD_API}/thumb/${encodeURIComponent(filename)}`;

/** The old 1000px JPEG, kept as a fallback while the thumb route rolls out. */
export const smallImageUrl = (filename: string) =>
  `${AOTD_API}/image-small/${encodeURIComponent(filename)}`;

export const SPRITES_URL = `${AOTD_API}/all/sprites`;

export const spriteUrl = (year: number, v: string) =>
  `${AOTD_API}/all/sprite/${year}.webp?v=${encodeURIComponent(v)}`;

export type DayKey = {
  year: number;
  /** 0-based month */
  month: number;
  /** 1-based day of the month */
  day: number;
  /** 0-based day of the year, the sprite cell */
  index: number;
};

const FILENAME_RE = /^AOTD-(\d{2})-(\d{2})-(\d{4})\.jpg$/;

/** Reads `AOTD-DD-MM-YYYY.jpg`; null for anything else. */
export function parseAotdFilename(filename: string | null | undefined): DayKey | null {
  if (!filename) return null;
  const m = FILENAME_RE.exec(filename);
  if (!m) return null;
  const day = Number(m[1]);
  const month = Number(m[2]) - 1;
  const year = Number(m[3]);
  const t = Date.UTC(year, month, day);
  const check = new Date(t);
  // Rejects 31-02 and friends instead of rolling them into March.
  if (check.getUTCMonth() !== month || check.getUTCDate() !== day) return null;
  const index = Math.round((t - Date.UTC(year, 0, 1)) / 86_400_000);
  return { year, month, day, index };
}

export type SpriteYear = { year: number; v: string; count: number };

export type SpriteMeta = {
  cell: number;
  cols: number;
  years: SpriteYear[];
};

/** Rows in a year sprite (366 cells, leap day included). */
export const spriteRows = (cols: number) => Math.ceil(366 / cols);

/**
 * Background style that shows one sprite cell scaled to fill its box.
 * Percent sizing keeps it right at any tile size, so the same call serves a
 * 14px calendar tile and a 400px blurred placeholder.
 */
export function spriteCellStyle(
  meta: SpriteMeta | null,
  key: DayKey | null,
): React.CSSProperties | null {
  if (!meta || !key) return null;
  const entry = meta.years.find((y) => y.year === key.year);
  if (!entry) return null;
  const rows = spriteRows(meta.cols);
  const col = key.index % meta.cols;
  const row = Math.floor(key.index / meta.cols);
  return {
    backgroundImage: `url("${spriteUrl(entry.year, entry.v)}")`,
    backgroundSize: `${meta.cols * 100}% ${rows * 100}%`,
    backgroundPosition: `${(col / (meta.cols - 1)) * 100}% ${(row / (rows - 1)) * 100}%`,
    backgroundRepeat: "no-repeat",
  };
}
